import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { admin, errorMessage } from "../lib/api";
import { useCurrency } from "../lib/auth";
import { fromMinor, money, toMinor } from "../lib/format";
import type { List, ShippingRate } from "../lib/types";
import { Alert, Badge, Button, Field, Modal, PageHeader, Spinner, Table, Toggle } from "../components/ui";

type Form = { id?: string; name: string; price: string; free_over: string; min_subtotal: string; max_subtotal: string; active: boolean; position: string };
const blank: Form = { name: "", price: "", free_over: "", min_subtotal: "", max_subtotal: "", active: true, position: "0" };

export default function Shipping() {
  const qc = useQueryClient();
  const currency = useCurrency();
  const list = useQuery({ queryKey: ["shipping"], queryFn: () => admin.get<List<ShippingRate>>("/shipping-rates") });
  const [form, setForm] = useState<Form | null>(null);
  const [error, setError] = useState<string>();

  const save = useMutation({
    mutationFn: (f: Form) => {
      const body = {
        name: f.name, price: toMinor(f.price) ?? 0, active: f.active, position: Number(f.position) || 0,
        ...(f.free_over ? { free_over: toMinor(f.free_over) } : {}),
        ...(f.min_subtotal ? { min_subtotal: toMinor(f.min_subtotal) } : {}),
        ...(f.max_subtotal ? { max_subtotal: toMinor(f.max_subtotal) } : {}),
      };
      return f.id ? admin.patch(`/shipping-rates/${f.id}`, body) : admin.post("/shipping-rates", body);
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["shipping"] }); setForm(null); },
    onError: (e) => setError(errorMessage(e)),
  });
  const del = useMutation({ mutationFn: (id: string) => admin.del(`/shipping-rates/${id}`), onSuccess: () => qc.invalidateQueries({ queryKey: ["shipping"] }) });
  const rows = list.data?.data ?? [];
  const m = (v: number | null) => (v == null ? "—" : money(v, currency));

  return (
    <>
      <PageHeader title="Metode de livrare" subtitle="Tarifele afișate clientului la checkout. Integrările cu curierii vor veni ulterior."
        actions={<Button onClick={() => { setError(undefined); setForm(blank); }}>+ Metodă</Button>} />
      {list.isLoading ? <Spinner /> : (
        <Table head={["Nume", "Preț", "Gratuit peste", "Interval subtotal", "Status", ""]} empty={rows.length === 0}>
          {rows.map((r) => (
            <tr key={r.id} className="hover:bg-slate-50">
              <td className="px-4 py-3 font-semibold">{r.name}</td>
              <td className="px-4 py-3">{m(r.price)}</td>
              <td className="px-4 py-3">{m(r.free_over)}</td>
              <td className="px-4 py-3 text-slate-500">{r.min_subtotal == null && r.max_subtotal == null ? "oricare" : `${m(r.min_subtotal)} – ${m(r.max_subtotal)}`}</td>
              <td className="px-4 py-3">{r.active ? <Badge tone="green">Activă</Badge> : <Badge>Inactivă</Badge>}</td>
              <td className="px-4 py-3 text-right">
                <Button size="sm" variant="ghost" onClick={() => {
                  setError(undefined);
                  setForm({ id: r.id, name: r.name, price: fromMinor(r.price), free_over: fromMinor(r.free_over), min_subtotal: fromMinor(r.min_subtotal),
                    max_subtotal: fromMinor(r.max_subtotal), active: r.active, position: String(r.position) });
                }}>Editează</Button>
                <Button size="sm" variant="ghost" onClick={() => confirm(`Ștergi „${r.name}”?`) && del.mutate(r.id)}>Șterge</Button>
              </td>
            </tr>
          ))}
        </Table>
      )}
      <Modal open={!!form} onClose={() => setForm(null)} title={form?.id ? "Editează metoda" : "Metodă de livrare nouă"}>
        {form && (
          <form className="space-y-4" onSubmit={(e) => { e.preventDefault(); save.mutate(form); }}>
            <Field label="Nume"><input className="input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Curier rapid" /></Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Preț (lei)"><input className="input" required inputMode="decimal" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} /></Field>
              <Field label="Gratuit peste (lei)"><input className="input" inputMode="decimal" value={form.free_over} onChange={(e) => setForm({ ...form, free_over: e.target.value })} /></Field>
              <Field label="Subtotal minim"><input className="input" inputMode="decimal" value={form.min_subtotal} onChange={(e) => setForm({ ...form, min_subtotal: e.target.value })} /></Field>
              <Field label="Subtotal maxim"><input className="input" inputMode="decimal" value={form.max_subtotal} onChange={(e) => setForm({ ...form, max_subtotal: e.target.value })} /></Field>
            </div>
            <Toggle label="Activă" checked={form.active} onChange={(v) => setForm({ ...form, active: v })} />
            {error && <Alert>{error}</Alert>}
            <div className="flex justify-end gap-2">
              <Button type="button" variant="secondary" onClick={() => setForm(null)}>Anulează</Button>
              <Button disabled={save.isPending}>Salvează</Button>
            </div>
          </form>
        )}
      </Modal>
    </>
  );
}
